import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Phone, XCircle } from "lucide-react";
import { api, fmt } from "@/lib/api";

export default function CheckoutResult({ success }) {
  const [order, setOrder] = useState(null);
  const params = new URLSearchParams(window.location.search);
  const id = params.get("order_id");

  useEffect(() => {
    if (!id) return;
    let stopped = false;
    let tries = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/orders/${id}`);
        if (stopped) return;
        setOrder(r.data);
        if (r.data.status === "PENDING" && tries++ < 10) {
          setTimeout(poll, 2000);
        }
      } catch {
        /* keep waiting */
      }
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [id]);

  const status = order?.status || "PENDING";
  const paid = status === "PAID" || status === "PREPARING" || status === "READY" || status === "COMPLETED";
  const failed = status === "PAYMENT_FAILED" || status === "CHECKOUT_ERROR" || (!success && !paid);

  return (
    <div data-testid="checkout-result-page" className="flex min-h-screen items-center justify-center bg-sage px-4 font-body">
      <div className="w-full max-w-md rounded-3xl border border-leafline bg-cream p-8 text-center shadow-xl">
        {paid ? (
          <>
            <CheckCircle2 className="mx-auto h-16 w-16 text-artichoke" data-testid="result-icon-paid" />
            <h1 className="mt-4 font-display text-3xl font-bold text-deep">Thank you for ordering!</h1>
            <p className="mt-3 text-sm leading-relaxed text-chalk/60">
              {order?.customer?.name ? `${order.customer.name}, your` : "Your"} order is confirmed
              and the kitchen has it. Pickup {order?.pickup_time || "ASAP"} at 11221 Merritt St,
              Castroville — show this screen at the counter.
            </p>
            {order && (
              <div className="mt-5 rounded-2xl bg-sage p-4 text-left">
                {order.items.map((it) => (
                  <div key={it.item_id} className="flex justify-between py-1 text-sm">
                    <span>{it.qty} × {it.title}</span>
                    <span className="font-semibold">{fmt(it.unit_price_cents * it.qty)}</span>
                  </div>
                ))}
                <div className="mt-2 flex justify-between border-t border-dashed border-chalk/15 pt-2 font-display text-lg font-bold">
                  <span>Total</span>
                  <span data-testid="result-total">{fmt(order.total_cents)}</span>
                </div>
              </div>
            )}
          </>
        ) : failed ? (
          <>
            <XCircle className="mx-auto h-16 w-16 text-crispy" data-testid="result-icon-failed" />
            <h1 className="mt-4 font-display text-3xl font-bold text-deep">Payment didn't go through</h1>
            <p className="mt-3 text-sm leading-relaxed text-chalk/60">
              No charge was made. You can try ordering again, or just call us and
              we'll take your order over the phone.
            </p>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-16 w-16 animate-spin text-artichoke" data-testid="result-icon-pending" />
            <h1 className="mt-4 font-display text-3xl font-bold text-deep">Confirming your payment…</h1>
            <p className="mt-3 text-sm text-chalk/60">Hang tight — this takes a few seconds. Don't close this page.</p>
          </>
        )}

        <div className="mt-7 flex flex-col gap-3">
          {paid && (
            <a
              data-testid="result-order-again-button"
              href="/order"
              className="rounded-full bg-artichoke py-3.5 font-display text-base font-semibold text-cream transition-colors hover:bg-deep"
            >
              Order Something Else
            </a>
          )}
          {failed && (
            <a
              data-testid="result-try-again-button"
              href="/order"
              className="rounded-full bg-artichoke py-3.5 font-display text-base font-semibold text-cream transition-colors hover:bg-deep"
            >
              Try Ordering Again
            </a>
          )}
          <a
            data-testid="result-home-link"
            href="/"
            className={`${paid || failed ? "text-sm font-bold text-artichoke hover:text-grape" : "rounded-full bg-artichoke py-3.5 font-display text-base font-semibold text-cream hover:bg-deep"} transition-colors`}
          >
            Back to Giant Artichoke
          </a>
          <a
            data-testid="result-call-link"
            href="tel:8316334259"
            className="inline-flex items-center justify-center gap-2 text-sm font-bold text-artichoke hover:text-grape"
          >
            <Phone className="h-4 w-4" /> Questions? (831) 633-4259
          </a>
        </div>
      </div>
    </div>
  );
}
